/**
 * Sidebar Component
 * Navigation sidebar with scenario list and progress
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { useProgress } from '../../contexts/ProgressContext';
import { useAuth } from '../../contexts/AuthContext';
import { Home, Target, TrendingUp, X, LogOut, User, Lock } from 'lucide-react';
import { canAccessScenario } from '../../utils/scenarioSequencing';

const Sidebar = ({ isOpen, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { progress } = useProgress();
  const { user, signOut } = useAuth();

  const scenarios = [
    { id: 'saas-1', title: 'Discovery & Definition', track: 'SaaS', difficulty: 'Beginner' },
    { id: 'saas-2', title: 'Agile Sprint Planning', track: 'SaaS', difficulty: 'Beginner' },
    { id: 'saas-3', title: 'Design Collaboration', track: 'SaaS', difficulty: 'Intermediate' },
    { id: 'saas-4', title: 'Development & Standups', track: 'SaaS', difficulty: 'Intermediate' },
    { id: 'saas-5', title: 'Launch & Metrics', track: 'SaaS', difficulty: 'Advanced' },
    { id: 'ai-1', title: 'AI Feature Discovery', track: 'AI', difficulty: 'Intermediate' },
    { id: 'ai-2', title: 'Prompt Engineering', track: 'AI', difficulty: 'Advanced' },
    { id: 'ai-3', title: 'Model Performance', track: 'AI', difficulty: 'Advanced' },
  ];

  const isCompleted = (scenarioId) => {
    return progress?.completedScenarios?.some(s => s.id === scenarioId);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
      onMobileClose(); // Close mobile menu if open
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Get user display name (full_name from metadata or email)
  const getUserDisplayName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    return user?.email || 'User';
  };

  // Get user email
  const getUserEmail = () => {
    return user?.email || '';
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={onMobileClose} />}
      <aside className={`sidebar ${isOpen ? 'open' : 'closed'} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <h2>Scenarios</h2>
          <button className="sidebar-close" onClick={onMobileClose}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <Link 
            to="/app" 
            className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
            onClick={onMobileClose}
          >
            <Home size={18} />
            <span>Dashboard</span>
          </Link>

          <div className="nav-section">
            <h3 className="nav-section-title">SaaS Track</h3>
            {scenarios.filter(s => s.track === 'SaaS').map(scenario => {
              const accessCheck = canAccessScenario(scenario.id, progress?.completedScenarios || []);
              const isLocked = !accessCheck.accessible;
              
              if (isLocked) {
                return (
                  <div
                    key={scenario.id}
                    className="nav-item scenario-item locked"
                    title={accessCheck.reason || 'Complete previous scenarios first'}
                  >
                    <Lock size={16} />
                    <span>{scenario.title}</span>
                    <span className="lock-badge">Locked</span>
                  </div>
                );
              }
              
              return (
                <Link
                  key={scenario.id}
                  to={`/scenario/${scenario.id}`}
                  className={`nav-item scenario-item ${location.pathname.includes(scenario.id) ? 'active' : ''} ${isCompleted(scenario.id) ? 'completed' : ''}`}
                  onClick={onMobileClose}
                >
                  <Target size={16} />
                  <span>{scenario.title}</span>
                  {isCompleted(scenario.id) && <span className="checkmark">✓</span>}
                </Link>
              );
            })}
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">AI Track</h3>
            {scenarios.filter(s => s.track === 'AI').map(scenario => (
              <div
                key={scenario.id}
                className="nav-item scenario-item coming-soon"
                onClick={(e) => {
                  e.preventDefault();
                  alert('Coming Soon! The AI Track will be available in a future update.');
                }}
                title="Coming Soon"
              >
                <TrendingUp size={16} />
                <span>{scenario.title}</span>
                <span className="coming-soon-badge">Coming Soon</span>
              </div>
            ))}
          </div>
        </nav>

        {/* User Section at Bottom */}
        {user && (
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">
                <User size={18} />
              </div>
              <div className="user-details">
                <div className="user-name">{getUserDisplayName()}</div>
                <div className="user-email">{getUserEmail()}</div>
              </div>
            </div>
            <button 
              className="logout-button"
              onClick={handleLogout}
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;





